import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { TrendingUp, TrendingDown, DollarSign, Shield, Target, Activity } from 'lucide-react'

interface TodayStats {
  totalLeads: number
  fraudBlocked: number
  moneySaved: number
  detectionRate: number
  falsePositives: number
  avgDetectionTime: number
}

interface FinancialImpactGridProps {
  stats?: TodayStats
}

export const FinancialImpactGrid: React.FC<FinancialImpactGridProps> = ({ stats }) => {
  // Default values based on the business context
  const defaultStats: TodayStats = {
    totalLeads: 247,
    fraudBlocked: 79,
    moneySaved: 2370,
    detectionRate: 95.3,
    falsePositives: 1.8,
    avgDetectionTime: 47
  }

  const data = stats || defaultStats
  const fraudRate = ((data.fraudBlocked / data.totalLeads) * 100)
  const monthlyProjection = data.moneySaved * 30 // Rough monthly projection

  const metrics = [
    {
      title: "Today's Leads",
      value: data.totalLeads.toLocaleString(),
      subtitle: `${data.fraudBlocked} fraudulent (${fraudRate.toFixed(1)}%)`,
      trend: "+12%",
      trendPositive: false,
      icon: Activity,
      color: "text-blue-600"
    },
    {
      title: "Detection Rate",
      value: `${data.detectionRate}%`,
      subtitle: `${data.falsePositives}% false positives`,
      trend: "+0.5%",
      trendPositive: true,
      icon: Target,
      color: "text-green-600"
    },
    {
      title: "Saved Today",
      value: `$${data.moneySaved.toLocaleString()}`,
      subtitle: `$${monthlyProjection.toLocaleString()} monthly pace`,
      trend: "+$420",
      trendPositive: true,
      icon: DollarSign,
      color: "text-green-600"
    },
    {
      title: "Active Patterns",
      value: "4",
      subtitle: "23 IPs blocked",
      trend: "2 new",
      trendPositive: false,
      icon: Shield,
      color: "text-orange-600"
    }
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {metrics.map((metric, index) => {
        const IconComponent = metric.icon
        return (
          <Card key={index} className="relative overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <IconComponent className={`h-6 w-6 ${metric.color}`} />
                <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                  metric.trendPositive 
                    ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20' 
                    : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20'
                }`}>
                  {metric.trendPositive ? (
                    <TrendingUp className="inline h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="inline h-3 w-3 mr-1" />
                  )}
                  {metric.trend}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                  {metric.title}
                </h3>
                <p className="text-2xl font-bold text-foreground mb-1">
                  {metric.value}
                </p>
                <p className="text-sm text-muted-foreground">
                  {metric.subtitle}
                </p>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}